// this util is referenced in cypress/
// absolute references don't work in the cypress folder

import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { getSelectedWarehouse } from '.';
import { Tense } from '../graphql/types/delivery';
import { GScheduledDeliveryTime } from '../graphql/types/generated';
import { DeliveryState } from '../state/slices/delivery';

const PLUS_ONE_DAY_STR = ' (+1 day)';

// moving this to it own file to avoid tests breaking
export const getModifiedIsoDate = (
  isoDate: string,
  isoTime?: string,
): string => {
  // (2020-08-10T12:00PM, 6:00PM) -> 2020-08-10T6:00PM
  if (isoTime) {
    return `${isoDate.split('T')[0]}T${isoTime}`;
  }
  return isoDate.split('T')[0];
};

export const isSameDayDeliveryWindow = (startTime = ''): boolean => {
  const SUBSTRING = 'PM';
  const time = formatTime24To12Hour(startTime);
  return !time.includes(SUBSTRING);
};

export const isDateTimeAfter = ({
  deliveredAt,
  endTime,
  viewingDate,
  timeZone,
}: {
  deliveredAt: string;
  endTime?: string;
  viewingDate: string;
  timeZone?: string;
}): boolean => {
  if (!timeZone || !endTime) return false;
  const endMs = zonedTimeToUtc(
    new Date(`${viewingDate} ${endTime}`),
    timeZone,
  ).getTime();
  const deliveredMs = Date.parse(deliveredAt);
  return deliveredMs > endMs;
};

export const padToTwo = (num: string): string => {
  return num.padStart(2, '0');
};

/**
 * This is used to make sure when we pass the raw date YYYY-MM-DD into the new Date(date)
 * function it doesn't convert it to a past date due to your timezone
 * i.e. without this function if you use `new Date("2022-09-01")` in PST it will
 * return a date of `Wed Aug 31 2022 17:00:00 GMT-0700 (Pacific Daylight Time)`
 * but if you use `adjustForTimezone(new Date("2022-09-01"))` in PST it will
 * return `Thu Sep 01 2022 00:00:00 GMT-0700 (Pacific Daylight Time)`
 * @param  {Date} date Date object
 * @returns new Date object
 */
export const adjustForTimezone = (date: Date): Date => {
  const timeOffsetInMS: number = date.getTimezoneOffset() * 60000;
  date.setTime(date.getTime() + timeOffsetInMS);
  return date;
};

export const getYearMonthDay = (date: Date): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const monthStr = month > 9 ? month : `0${month}`;
  const day = date.getDate();
  const dayStr = day > 9 ? day : `0${day}`;
  return `${year}-${monthStr}-${dayStr}`;
};

// --- "yyyy-MM-dd" week navigation helpers (non-mutating; addDays mutates its
// input). Shared by the ProcessPathAdmin and DockBookingAdmin week views.

export const parseYmd = (ymd: string): Date => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const plusDays = (ymd: string, days: number): string => {
  const date = parseYmd(ymd);
  date.setDate(date.getDate() + days);
  return getYearMonthDay(date);
};

export const mondayOf = (ymd: string): string => {
  const date = parseYmd(ymd);
  return plusDays(ymd, -((date.getDay() + 6) % 7));
};

export const MONTH_DAY = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

// "Aug 3 – 9" / "Jul 28 – Aug 3" — compact inclusive date range, month elided
// on the end date when it matches the start.
export const rangeLabel = (startYmd: string, endYmd: string): string => {
  const start = parseYmd(startYmd);
  const end = parseYmd(endYmd);
  const endLabel =
    start.getMonth() === end.getMonth() ? end.getDate() : MONTH_DAY.format(end);
  return `${MONTH_DAY.format(start)} – ${endLabel}`;
};

export const weekLabel = (weekStart: string): string =>
  rangeLabel(weekStart, plusDays(weekStart, 6));

export const WEEKDAY_FMT = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
});

export const todayYmd = (tz = 'America/New_York'): string =>
  formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');

// ICU 72+ (bundled with some Node versions, e.g. the CI Node) inserts a narrow
// no-break space (U+202F) before the meridiem in locale time formatting, while
// older/newer runtimes use a plain space. Normalize U+202F and NBSP to a plain
// space so formatted times are identical across Node/ICU versions and match
// what the rest of the app and the tests expect.
const normalizeSpaces = (value: string): string =>
  value.replace(/[\u202f\u00a0]/g, ' ');

export const formatISO8601Date = (date: string): string => {
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return date;

  return Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
};

export const formatISO8601DateTime = (
  timestamp: string,
  timeZone?: string,
): string => {
  const dateTime = Date.parse(timestamp);

  // [MMM] [DoM], [HH]:[mm] [meridiem] [TZ]
  return normalizeSpaces(
    Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timeZone,
      timeZoneName: 'short',
    }).format(dateTime),
  );
};

// formats time from military to regular eg. "18:00" -> "6:00PM"
export const formatTime24To12Hour = (time: String): String => {
  const timeString12hr = new Date(
    '1970-01-01T' + time + 'Z',
  ).toLocaleTimeString('en-US', {
    timeZone: 'UTC',
    hour12: true,
    hour: 'numeric',
    minute: 'numeric',
  });
  return normalizeSpaces(timeString12hr);
};

// Shown in place of a GPS-sourced timestamp (arrival/departure, GPS trail
// points) when the device never reported a location — distinct from a plain
// "--", which reads as "hasn't happened yet" rather than "couldn't be
// determined." Shared so every GPS-timestamp display uses identical copy.
export const GPS_DATA_UNAVAILABLE_TEXT = 'GPS data unavailable';

export const formatDateTo12Hour = (date: Date, timeZone?: string): string => {
  return normalizeSpaces(
    date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      // Omitted → renders in the viewer's local zone; pass the location/
      // warehouse zone to show the time where the stop actually happened.
      timeZone,
    }),
  );
};

// "Jul 16, 2026, 9:24 PM" in the given timezone — the full date + time shown
// on hover over a timeline time, so a cross-day event (e.g. an overnight
// route) is identifiable without cluttering the row.
export const formatFullDateTime = (date: Date, timeZone?: string): string => {
  return normalizeSpaces(
    date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone,
    }),
  );
};

// "Apr 25, 4:00 PM" — used when the timestamp may be future-dated and
// time-only would be ambiguous (e.g. the receiving editing window).
export const formatDateAndTimeTo12Hour = (date: Date): string => {
  return normalizeSpaces(
    date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }),
  );
};

export const addDays = (days: number, currentDate: Date): Date => {
  let futureDate: Date = currentDate;
  futureDate.setDate(currentDate.getDate() + days);
  return futureDate;
};

// returns now in UTC as YYYY-MM-DDThh:mm:ss.sss+00:00 ex. 2022-09-13T20:57:36.070+00:00
export const getRubyFriendlyUtcStr = (): string => {
  const strToReplace = 'Z';
  const appendUTCStr = '+00:00';
  const now = new Date(Date.now());
  const utc = now.toISOString();
  const rubyFriendlyUtc = utc.replace(strToReplace, appendUTCStr);
  return rubyFriendlyUtc;
};

// formats a duration in seconds as "Xh Ym Zs", dropping leading zero units
// (seconds always shown). Returns "—" for null/undefined/negative input.
// e.g. 323 -> "5m 23s", 45 -> "45s", 3661 -> "1h 0m 1s", 0 -> "0s"
export const formatDurationInSeconds = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined || seconds < 0) return '—';

  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (hours > 0 || minutes > 0) parts.push(`${minutes}m`);
  parts.push(`${secs}s`);

  return parts.join(' ');
};

export const getDateStr = (isoDate: string): string => {
  const date = adjustForTimezone(new Date(isoDate));
  const month = date.toLocaleString('default', { month: 'long' });
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
};

// determine if the user is in the past present or future based on the warehouse's night end.
export const pastPresentFutureDate = (
  delivery: DeliveryState,
  nightEnd?: string,
): Tense | undefined => {
  if (!nightEnd) return undefined;
  const selectedWarehouse = getSelectedWarehouse(
    delivery.selectedWarehouseId,
    delivery.warehouses,
  );
  if (!selectedWarehouse) return undefined;

  const todayStr = selectedWarehouse.currentNightEnd
    .split('T')[0]
    .replaceAll('-', '/');
  const today = new Date(todayStr);
  const viewing = adjustForTimezone(new Date(nightEnd));
  if (new Date(today) > new Date(viewing)) return Tense.past;
  if (new Date(today) < new Date(viewing)) return Tense.future;
  return Tense.present;
};

export const isDateBeforeCurrentWarehouseNightEnd = (
  delivery: DeliveryState,
  date: Date,
): boolean => {
  const selectedWarehouse = getSelectedWarehouse(
    delivery.selectedWarehouseId,
    delivery.warehouses,
  );
  if (!selectedWarehouse) return false;

  const currentWarehouseNightEnd = new Date(selectedWarehouse.currentNightEnd);

  return date < currentWarehouseNightEnd;
};

export const isMultiDayDeliveryWindow = (scheduledDeliveryTime: {
  earliest: string;
  latest: string;
  timeZone: string;
}): boolean => {
  const { earliest, latest, timeZone } = scheduledDeliveryTime;
  const earliestDate = new Date(earliest);
  const latestDate = new Date(latest);

  const earliestDateStr = earliestDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const latestDateStr = latestDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  return earliestDateStr !== latestDateStr;
};

export const getHHMMSSTimeFromDateString = (
  dateString: string,
  timeZone: string,
): string | undefined => {
  const timestamp = Date.parse(dateString);
  if (isNaN(timestamp)) return undefined;

  return Intl.DateTimeFormat('en-US-u-hc-h23', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: timeZone,
  }).format(timestamp);
};

export const addPlusOneDayStringToPMTime = (startTime?: string): String => {
  if (!startTime) return '';
  return isSameDayDeliveryWindow(startTime) ? '' : PLUS_ONE_DAY_STR;
};

const formatDateToYYYYMMDD = (date: Date, timeZone: string): string => {
  const dateFormatOptions = {
    timeZone: timeZone,
  };

  return date.toLocaleDateString('sv-SE', dateFormatOptions);
};

const buildScheduledDeliveryWindowString = ({
  earliestTimeStr,
  latestTimeStr,
  isMultiDay,
  plusOneDayString,
  showDate,
  earliestDateStr,
  latestDateStr,
}: {
  earliestTimeStr: string;
  latestTimeStr: string;
  isMultiDay: boolean;
  plusOneDayString: string;
  showDate: boolean;
  earliestDateStr?: string;
  latestDateStr?: string;
}): string => {
  if (showDate && earliestDateStr && latestDateStr) {
    return isMultiDay
      ? `${earliestTimeStr} (${earliestDateStr}) - ${latestTimeStr}${plusOneDayString} (${latestDateStr})`
      : `${earliestTimeStr} (${earliestDateStr}) - ${latestTimeStr}`;
  }

  return isMultiDay
    ? `${earliestTimeStr} - ${latestTimeStr}${plusOneDayString}`
    : `${earliestTimeStr} - ${latestTimeStr}`;
};

// Helper function to extract common delivery window processing logic
export const processDeliveryWindow = ({
  locationDeliveryWindow,
  scheduledDeliveryTime,
  viewingNightEnd,
  useScheduledDeliveryTime = false,
}: {
  locationDeliveryWindow?: {
    startTime: string;
    endTime: string;
  };
  scheduledDeliveryTime?: GScheduledDeliveryTime;
  viewingNightEnd?: string;
  useScheduledDeliveryTime?: boolean;
}): {
  startTime24: string;
  endTime24: string;
  startTime12: string;
  endTime12: string;
  isMultiDay: boolean;
  plusOneDayString: string;
  earliestDate?: Date;
  latestDate?: Date;
  timeZone?: string;
  viewingNightEnd?: string;
  previousDateStr?: string;
} | null => {
  if (useScheduledDeliveryTime) {
    if (!scheduledDeliveryTime) {
      return null; // Represents 'CLOSED' state
    }

    const { earliest, latest, timeZone } = scheduledDeliveryTime;
    const earliestDate = new Date(earliest);
    const latestDate = new Date(latest);
    const isMultiDay = isMultiDayDeliveryWindow(scheduledDeliveryTime);

    return {
      startTime24: earliest.split('T')[1]?.slice(0, 5) || '',
      endTime24: latest.split('T')[1]?.slice(0, 5) || '',
      startTime12: formatDateTo12Hour(earliestDate),
      endTime12: formatDateTo12Hour(latestDate),
      isMultiDay,
      plusOneDayString: isMultiDay ? PLUS_ONE_DAY_STR : '',
      earliestDate,
      latestDate,
      timeZone,
    };
  }

  const startTime = locationDeliveryWindow?.startTime;
  const endTime = locationDeliveryWindow?.endTime;
  const isMultiDay = !isSameDayDeliveryWindow(startTime);

  let previousDateStr = '';
  if (viewingNightEnd) {
    const previousDate = adjustForTimezone(
      addDays(-1, new Date(viewingNightEnd)),
    );
    previousDateStr = getYearMonthDay(previousDate);
  }

  return {
    startTime24: startTime || '',
    endTime24: endTime || '',
    startTime12: formatTime24To12Hour(startTime ?? '') as string,
    endTime12: formatTime24To12Hour(endTime ?? '') as string,
    isMultiDay,
    plusOneDayString: addPlusOneDayStringToPMTime(startTime) as string,
    viewingNightEnd,
    previousDateStr,
  };
};

export const getDeliveryWindowTimes = ({
  locationDeliveryWindow,
  scheduledDeliveryTime,
  viewingNightEnd,
  useScheduledDeliveryTime = false,
}: {
  locationDeliveryWindow: {
    startTime: string;
    endTime: string;
  };
  scheduledDeliveryTime?: GScheduledDeliveryTime;
  viewingNightEnd?: string;
  useScheduledDeliveryTime?: boolean;
}): { startTime: string; endTime: string } | null => {
  const processedWindow = processDeliveryWindow({
    locationDeliveryWindow,
    scheduledDeliveryTime,
    viewingNightEnd,
    useScheduledDeliveryTime,
  });

  if (!processedWindow) {
    return null; // Represents 'CLOSED' state
  }

  return {
    startTime: processedWindow.startTime24,
    endTime: processedWindow.endTime24,
  };
};

export const getDeliveryWindowStr = ({
  locationDeliveryWindow,
  scheduledDeliveryTime,
  showDate,
  viewingNightEnd,
  useScheduledDeliveryTime = false,
}: {
  locationDeliveryWindow: {
    startTime: string;
    endTime: string;
  };
  scheduledDeliveryTime?: GScheduledDeliveryTime;
  showDate: boolean;
  viewingNightEnd?: string;
  useScheduledDeliveryTime?: boolean;
}): string => {
  const processedWindow = processDeliveryWindow({
    locationDeliveryWindow,
    scheduledDeliveryTime,
    viewingNightEnd,
    useScheduledDeliveryTime,
  });

  if (!processedWindow) {
    return 'CLOSED';
  }

  if (useScheduledDeliveryTime) {
    const {
      startTime12,
      endTime12,
      isMultiDay,
      plusOneDayString,
      earliestDate,
      latestDate,
      timeZone,
    } = processedWindow;

    if (showDate && earliestDate && latestDate && timeZone) {
      const earliestDateStr = formatDateToYYYYMMDD(earliestDate, timeZone);
      const latestDateStr = formatDateToYYYYMMDD(latestDate, timeZone);

      return buildScheduledDeliveryWindowString({
        earliestTimeStr: startTime12,
        latestTimeStr: endTime12,
        isMultiDay,
        plusOneDayString,
        showDate: true,
        earliestDateStr,
        latestDateStr,
      });
    }

    return buildScheduledDeliveryWindowString({
      earliestTimeStr: startTime12,
      latestTimeStr: endTime12,
      isMultiDay,
      plusOneDayString,
      showDate: false,
    });
  }

  const { startTime12, endTime12, plusOneDayString, previousDateStr } =
    processedWindow;
  const startTime = locationDeliveryWindow.startTime;

  const getFormatStartDate = (): string => {
    if (isSameDayDeliveryWindow(startTime)) return ` (${viewingNightEnd})`;
    return ` (${previousDateStr})`;
  };

  const formatStartDate = showDate ? getFormatStartDate() : '';
  const formatEndDate = showDate ? ` (${viewingNightEnd})` : '';
  const deliveryWindowLabel = `${startTime12}${formatStartDate} - ${endTime12}${plusOneDayString}${formatEndDate}`;
  return deliveryWindowLabel;
};
